import { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Debug from 'debug'
import { observer } from 'mobx-react-lite'

import type { Photos } from '@jotta/types/Photos'

import type { CollectionRef } from '../../api/useCollection'
import type { PhotoActionType } from '../PhotoActions/PhotoActions.types'

import type { MediaCarouselProps } from './MediaCarousel'
import {
  MediaCarousel,
  type MediaCarouselActionScope,
  type MediaCarouselItemQueryOptions,
  type MediaCarouselKeyField,
  type MediaCarouselPreview,
} from './MediaCarousel'

const debug = Debug('jotta:photos:MediaCarousel')

function useNavigateBack(mediaId?: string) {
  const navigate = useNavigate()
  // If no mediaId when mounted, we can safely navigate back to timeline/album
  const canNavigateBack = useRef(!mediaId)

  return useCallback(
    (open: boolean) => {
      if (!open) {
        debug('close')
        if (window.history.length > 1 && canNavigateBack.current && mediaId) {
          navigate(-1)
        } else {
          navigate('./', { replace: true })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediaId],
  )
}

export const MediaCarouselContainer = observer<{
  collectionRef: CollectionRef
  mediaId?: string
  keyField?: MediaCarouselKeyField
  itemQueryOptions?: (key: string) => MediaCarouselItemQueryOptions | undefined
  ids?: readonly string[]
  items?: readonly Photos.Media[]
  previewItems?: readonly MediaCarouselPreview[]
  showOwnerName?: boolean
  showSimilarSearch?: boolean
  navOnQueryParam?: string
  actionScope?: MediaCarouselActionScope
  onTrackAction?: (actionType: PhotoActionType) => void
  onNavigate?: (item?: { id: string; timestamp: bigint }) => void
  onCurrentMediaChange?: (id: string) => void
  onClose?: (closed: boolean) => void
}>(function MediaCarouselContainer({
  collectionRef,
  mediaId,
  keyField,
  itemQueryOptions,
  ids = [],
  items = [],
  previewItems,
  showOwnerName,
  showSimilarSearch = false,
  navOnQueryParam,
  actionScope,
  onTrackAction,
  onNavigate,
  onCurrentMediaChange,
  onClose,
}) {
  const navigateBack = useNavigateBack(mediaId)
  const open = Boolean(mediaId)

  const carouselProps: MediaCarouselProps = {
    collectionRef,
    open,
    ids,
    items,
    previewItems,
    currentId: mediaId,
    keyField,
    itemQueryOptions,
    slideShowMode: false,
    onNavigate,
    onCurrentMediaChange,
    onClose: onClose || navigateBack,
    showOwnerName,
    showSimilarSearch,
    navOnQueryParam,
    actionScope,
    onTrackAction,
  }

  if (!mediaId) {
    return null
  }

  return <MediaCarousel {...carouselProps} />
})
